import Close from "@mui/icons-material/Close";
import { Alert, Box, Button, CircularProgress, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  removeNotification,
  pushNotification,
  type NotificationItem,
  type NotificationDownloadAction,
} from "features/notificationSlice";
import { removeBrowserZipJob } from "features/browserZipSlice";
import { removeFolderConnectSync } from "features/folderConnectSyncSlice";
import {
  suppressTopFolderZipProgressBanner,
  clearTopFolderZipReady,
} from "features/topFolderZipSlice";
import { useLazyGetTopFolderZipDownloadQuery } from "features/fileSystem/filesStorage";
import { useAppDispatch, useAppSelector } from "features/store";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";

type ActivityRow = {
  id: string;
  message: string;
  severity: "info";
  variant: "loading";
  source: "activity";
  onDismiss: () => void;
};

type ToastRow = NotificationItem & { source: "toast" };

type StackRow = ToastRow | ActivityRow;

const stackWidthPx = { xs: "calc(100vw - 32px)", sm: 420 } as const;

const closeButtonProps = {
  size: "small" as const,
  disableRipple: true,
  sx: {
    p: "2px",
    width: 26,
    height: 26,
    minWidth: 26,
    minHeight: 26,
    borderRadius: 1,
    color: "text.secondary",
    "&:hover": { bgcolor: "action.hover" },
    "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
  },
};

const alertPaperSx = {
  width: "100%",
  alignItems: "center",
  py: 1,
  px: 1.5,
  fontWeight: 600,
  boxShadow: 3,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  "& .MuiAlert-icon": {
    mr: 1,
    py: 0,
    alignItems: "center",
    opacity: 1,
  },
  "& .MuiAlert-message": {
    flex: "1 1 0%",
    minWidth: 0,
    py: 0,
    "& .MuiTypography-root": {
      margin: 0,
      fontWeight: 600,
      wordBreak: "break-word" as const,
      display: "-webkit-box",
      WebkitBoxOrient: "vertical" as const,
      WebkitLineClamp: 3,
      overflow: "hidden",
    },
  },
  "& .MuiAlert-action": {
    marginRight: 0,
    padding: 0,
    pl: 1,
    alignItems: "center",
  },
};

const loadingAlertSx = {
  "& .MuiAlert-icon": {
    mr: 1.25,
  },
  "& .MuiAlert-message .MuiTypography-root": {
    WebkitLineClamp: 4,
  },
};

export default function GlobalNotificationStack() {
  const dispatch = useAppDispatch();
  const [triggerTopZipDownload] = useLazyGetTopFolderZipDownloadQuery();
  const toastItems = useAppSelector((s) => s.notification.items);
  const topZipJobs = useAppSelector((s) => s.topFolderZip.jobs);
  const browserZipJobs = useAppSelector((s) => s.browserZip.jobs);
  const syncs = useAppSelector((s) => s.folderConnectSync.syncs);

  const handleNotificationAction = useCallback(
    async (notificationId: string, action: NotificationDownloadAction) => {
      if (action.kind === "pdf_split_view") {
        dispatch(removeNotification(notificationId));
        // The stack is mounted outside the router, so navigate via location.
        window.location.assign(`/splitting-tools/${action.jobId}`);
        return;
      }
      try {
        const dl = await triggerTopZipDownload(action.jobId).unwrap();
        startDownloadViaHiddenFrame(dl.download_url);
        dispatch(clearTopFolderZipReady({ itemKey: action.itemKey }));
        dispatch(removeNotification(notificationId));
      } catch {
        dispatch(
          pushNotification({
            message: "Could not start download. Try again.",
            severity: "error",
          }),
        );
      }
    },
    [triggerTopZipDownload, dispatch],
  );

  const activityRows: ActivityRow[] = useMemo(() => {
    const rows: ActivityRow[] = [];
    for (const [key, job] of Object.entries(topZipJobs)) {
      if (job.hideProgressBanner) continue;
      rows.push({
        id: `act-zip-${key}`,
        source: "activity",
        message: `Building ZIP "${job.label}"…`,
        severity: "info",
        variant: "loading",
        onDismiss: () =>
          dispatch(suppressTopFolderZipProgressBanner({ itemKey: key })),
      });
    }
    for (const [key, job] of Object.entries(browserZipJobs)) {
      rows.push({
        id: `act-bzip-${key}`,
        source: "activity",
        message: `Building ZIP "${job.label}"…`,
        severity: "info",
        variant: "loading",
        onDismiss: () => dispatch(removeBrowserZipJob({ itemKey: key })),
      });
    }
    for (const [syncIdStr, folderName] of Object.entries(syncs)) {
      const folderId = Number(syncIdStr);
      rows.push({
        id: `act-sync-${syncIdStr}`,
        source: "activity",
        message: folderName
          ? `Folder "${folderName}" is syncing…`
          : "Folder is syncing…",
        severity: "info",
        variant: "loading",
        onDismiss: () =>
          dispatch(removeFolderConnectSync({ folderId })),
      });
    }
    // PDF split jobs deliberately have no in-progress banner: runs take a long
    // time and a persistent overlay gets in the way. The poller still raises a
    // completion toast, and the Splitting Tools page shows live progress.
    return rows;
  }, [topZipJobs, browserZipJobs, syncs, dispatch]);

  const toastRows: ToastRow[] = useMemo(
    () => toastItems.map((t) => ({ ...t, source: "toast" as const })),
    [toastItems],
  );

  const rows: StackRow[] = [...activityRows, ...toastRows];

  const scheduledRef = useRef(new Set<string>());
  useEffect(() => {
    for (const item of toastItems) {
      if (item.autoHideMs == null || item.autoHideMs <= 0) continue;
      if (scheduledRef.current.has(item.id)) continue;
      scheduledRef.current.add(item.id);
      const { id } = item;
      const ms = item.autoHideMs;
      window.setTimeout(() => {
        dispatch(removeNotification(id));
        scheduledRef.current.delete(id);
      }, ms);
    }
  }, [toastItems, dispatch]);

  if (rows.length === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: { xs: 72, sm: 80 },
        right: { xs: 16, sm: 16 },
        zIndex: (theme) => theme.zIndex.snackbar,
        width: stackWidthPx,
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1,
        pointerEvents: "none",
        "& .MuiPaper-root": { pointerEvents: "auto" },
      }}
    >
      {rows.map((row) => {
        const isLoading = row.variant === "loading";
        const isActivity = row.source === "activity";
        const toastRow = row.source === "toast" ? row : null;
        const downloadAction = toastRow?.downloadAction;

        const dismissToast = () => dispatch(removeNotification(row.id));

        return (
          <Alert
            key={row.id}
            severity={isLoading ? "info" : row.severity}
            variant="standard"
            onClose={
              isActivity
                ? row.onDismiss
                : downloadAction
                  ? undefined
                  : dismissToast
            }
            action={
              downloadAction ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: "none", fontWeight: 600 }}
                    onClick={() =>
                      void handleNotificationAction(row.id, downloadAction)
                    }
                  >
                    {downloadAction.kind === "pdf_split_view"
                      ? "View results"
                      : "Download"}
                  </Button>
                  <IconButton
                    aria-label="Close notification"
                    onClick={dismissToast}
                    {...closeButtonProps}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ) : undefined
            }
            componentsProps={{
              closeButton: closeButtonProps,
            }}
            icon={
              isLoading ? (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress
                    size={24}
                    thickness={4}
                    sx={{ color: "primary.main" }}
                  />
                </Box>
              ) : undefined
            }
            sx={[
              alertPaperSx,
              ...(isLoading ? [loadingAlertSx] : []),
              (theme) => ({
                bgcolor: alpha(theme.palette.background.paper, 0.88),
                backdropFilter: "blur(10px)",
              }),
            ]}
          >
            {row.message}
          </Alert>
        );
      })}
    </Box>
  );
}
